// Pure diff helper for OI-6b's payload-diff preview.
//
// Computes the field-by-field delta between the current intake row
// (proposed_* + raw_payload + hold_reason) and the most recent entry
// in payload_history (the prior version before the most recent
// Hold-resubmit-merge). Returns a typed shape that the UI can render
// directly without any client-side branching on field types.
//
// Empty-history rows (first submission) return null so the UI can hide
// the panel entirely.

import type { OpsOrderIntakeRow } from "./orderIntakeClient";

/**
 * One labelled before/after pair. `prior` and `current` are pre-
 * rendered display strings — the helper normalizes scalars, dates,
 * and JSON objects up-front so the UI doesn't have to.
 */
export type IntakeDiffField = {
  key: string;
  label: string;
  prior: string;
  current: string;
  kind: "scalar" | "json";
};

export type IntakeDiff = {
  revised_at: string | null;
  prior_hold_reason: string | null;
  prior_decided_at: string | null;
  fields: IntakeDiffField[];
};

const FIELD_LABELS: Record<string, string> = {
  proposed_plant_id: "Proposed plant",
  proposed_seller_location_id: "Seller location",
  proposed_model_id: "Model",
  proposed_sale_path: "Sale path",
  proposed_inventory_holder_org_id: "Inventory holder org",
  proposed_end_customer_contact_id: "End customer contact",
  proposed_options: "Options",
  raw_payload: "Raw payload",
};

const SCALAR_FIELDS = [
  "proposed_plant_id",
  "proposed_seller_location_id",
  "proposed_model_id",
  "proposed_sale_path",
  "proposed_inventory_holder_org_id",
  "proposed_end_customer_contact_id",
] as const;

const JSON_FIELDS = ["proposed_options", "raw_payload"] as const;

/**
 * Compute the diff between a row's current state and the most recent
 * payload_history entry. Returns null when there is no history (first
 * submission) so the UI can skip rendering the panel entirely.
 */
export function computeIntakePayloadDiff(row: OpsOrderIntakeRow): IntakeDiff | null {
  const history = row.payload_history;
  if (!Array.isArray(history) || history.length === 0) return null;

  const prior = history[history.length - 1] as Record<string, unknown>;
  if (!prior || typeof prior !== "object") return null;

  const fields: IntakeDiffField[] = [];

  for (const k of SCALAR_FIELDS) {
    const priorVal = renderScalar(prior[k]);
    const currentVal = renderScalar(
      (row as unknown as Record<string, unknown>)[k]
    );
    if (priorVal !== currentVal) {
      fields.push({
        key: k,
        label: FIELD_LABELS[k] ?? k,
        prior: priorVal,
        current: currentVal,
        kind: "scalar",
      });
    }
  }

  for (const k of JSON_FIELDS) {
    const priorVal = renderJson(prior[k]);
    const currentVal = renderJson(
      (row as unknown as Record<string, unknown>)[k]
    );
    if (priorVal !== currentVal) {
      fields.push({
        key: k,
        label: FIELD_LABELS[k] ?? k,
        prior: priorVal,
        current: currentVal,
        kind: "json",
      });
    }
  }

  return {
    revised_at: renderScalarOrNull(prior.revised_at),
    prior_hold_reason: renderScalarOrNull(prior.hold_reason),
    prior_decided_at: renderScalarOrNull(prior.decided_at),
    fields,
  };
}

/**
 * Live draft-vs-row diff for the dealer Resubmit form (OI-5b / Q4).
 * Compares proposed fields the dealer is about to submit against the
 * current on_hold row (last submission), prefixed with hold_reason.
 */
export function computeDealerResubmitDraftDiff(
  row: OpsOrderIntakeRow,
  draft: {
    proposed_seller_location_id?: string | null;
    proposed_sale_path?: string | null;
    proposed_end_customer_contact_id?: string | null;
    proposed_model_id?: string | null;
    proposed_options?: Record<string, unknown>;
    raw_payload?: Record<string, unknown>;
  }
): IntakeDiff {
  const fields: IntakeDiffField[] = [];
  const pairs: Array<{ key: (typeof SCALAR_FIELDS)[number]; draftVal: unknown }> = [
    { key: "proposed_seller_location_id", draftVal: draft.proposed_seller_location_id ?? null },
    { key: "proposed_sale_path", draftVal: draft.proposed_sale_path ?? null },
    { key: "proposed_end_customer_contact_id", draftVal: draft.proposed_end_customer_contact_id ?? null },
    { key: "proposed_model_id", draftVal: draft.proposed_model_id ?? null },
  ];
  for (const { key, draftVal } of pairs) {
    const priorVal = renderScalar((row as unknown as Record<string, unknown>)[key]);
    const currentVal = renderScalar(draftVal);
    if (priorVal !== currentVal) {
      fields.push({
        key,
        label: FIELD_LABELS[key] ?? key,
        prior: priorVal,
        current: currentVal,
        kind: "scalar",
      });
    }
  }
  for (const k of JSON_FIELDS) {
    const priorVal = renderJson((row as unknown as Record<string, unknown>)[k]);
    const draftJson =
      k === "proposed_options" ? draft.proposed_options ?? {} : draft.raw_payload ?? {};
    const currentVal = renderJson(draftJson);
    if (priorVal !== currentVal) {
      fields.push({
        key: k,
        label: FIELD_LABELS[k] ?? k,
        prior: priorVal,
        current: currentVal,
        kind: "json",
      });
    }
  }
  return {
    revised_at: null,
    prior_hold_reason: row.hold_reason,
    prior_decided_at: row.decided_at,
    fields,
  };
}

function renderScalar(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v.trim() === "" ? "—" : v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return String(v);
}

function renderScalarOrNull(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "string") return v.trim() === "" ? null : v;
  return String(v);
}

/**
 * Stable JSON rendering: keys sorted recursively so cosmetically-
 * equivalent objects don't produce a spurious diff entry.
 */
function renderJson(v: unknown): string {
  if (v === null || v === undefined) return "—";
  try {
    return JSON.stringify(sortKeysDeep(v));
  } catch {
    return String(v);
  }
}

function sortKeysDeep(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortKeysDeep);
  if (v && typeof v === "object") {
    const entries = Object.entries(v as Record<string, unknown>)
      .map(([k, val]) => [k, sortKeysDeep(val)] as const)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    const out: Record<string, unknown> = {};
    for (const [k, val] of entries) out[k] = val;
    return out;
  }
  return v;
}
